/**
 * MobileSupervisorDashboard — Mobile supervisor quick-access dashboard.
 * Shows quick stats + feature buttons grid. Used by SupervisorLayout
 * as the universal mobile fallback for all supervisor routes.
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from 'react-query';
import { useLanguage } from '../i18n/LanguageContext';
import stationManagementService from '../services/stationManagementService';
import inventoryService from '../services/inventoryService';
import {
  LayoutGrid,
  ListChecks,
  ArrowLeftRight,
  Truck,
  Package,
  BarChart3,
  History,
  QrCode,
  Tag,
  ScanLine,
  X,
  Loader2,
} from 'lucide-react';

interface FeatureButton {
  id: string;
  icon: React.ReactNode;
  label: string;
  description: string;
  path: string;
  color: string;
  bgColor: string;
}

const MobileSupervisorDashboard: React.FC = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [showScanOptions, setShowScanOptions] = useState(false);
  const [showTransferOptions, setShowTransferOptions] = useState(false);

  const { data: stations, isLoading: stationsLoading } = useQuery(
    ['mobile-supervisor-stations'],
    () => stationManagementService.getStations(),
    { staleTime: 30000 }
  );

  const { data: productionSummary, isLoading: summaryLoading } = useQuery(
    ['mobile-production-summary'],
    () => inventoryService.getFinishedProductsSummary(),
    { staleTime: 30000 }
  );

  const activeSessions = stations?.filter(
    (s: any) => s.session_status === 'active' || s.session_status === 'in_progress'
  ).length || 0;

  const plannedSessions = stations?.filter(
    (s: any) => s.session_status === 'received' || s.session_status === 'ready'
  ).length || 0;

  const enabledStations = stations?.filter((s: any) => s.enabled !== false).length || 0;
  const totalStations = stations?.length || 0;
  const boxesToday = productionSummary?.boxes_today || 0;
  const isLoading = stationsLoading || summaryLoading;

  const features: FeatureButton[] = [
    {
      id: 'stations',
      icon: <LayoutGrid className="w-8 h-8" />,
      label: t('mobileSupervisor.stationOverview') || 'Station Overview',
      description: t('mobileSupervisor.stationOverviewDesc') || 'View all station status',
      path: '/supervisor/stations',
      color: 'text-blue-600 dark:text-blue-400',
      bgColor: 'bg-blue-100 dark:bg-blue-900/30',
    },
    {
      id: 'sessions',
      icon: <ListChecks className="w-8 h-8" />,
      label: t('mobileSupervisor.sessionManagement') || 'Sessions',
      description: t('mobileSupervisor.sessionManagementDesc') || 'Manage production sessions',
      path: '/supervisor/sessions',
      color: 'text-green-600 dark:text-green-400',
      bgColor: 'bg-green-100 dark:bg-green-900/30',
    },
    {
      id: 'transfer',
      icon: <Truck className="w-8 h-8" />,
      label: t('mobileSupervisor.transfers') || 'Transfers',
      description: t('mobileSupervisor.transfersDesc') || 'Receive & view transfers',
      path: '', // handled by onClick override
      color: 'text-amber-600 dark:text-amber-400',
      bgColor: 'bg-amber-100 dark:bg-amber-900/30',
    },
    {
      id: 'inventory',
      icon: <Package className="w-8 h-8" />,
      label: t('nav.inventory') || 'Inventory',
      description: t('mobileSupervisor.inventoryDesc') || 'View materials & stock',
      path: '/inventory',
      color: 'text-cyan-600 dark:text-cyan-400',
      bgColor: 'bg-cyan-100 dark:bg-cyan-900/30',
    },
    {
      id: 'summary',
      icon: <BarChart3 className="w-8 h-8" />,
      label: t('mobileSupervisor.endOfDaySummary') || 'Daily Summary',
      description: t('mobileSupervisor.endOfDaySummaryDesc') || 'Production stats',
      path: '/supervisor/reports?tab=end-of-day',
      color: 'text-indigo-600 dark:text-indigo-400',
      bgColor: 'bg-indigo-100 dark:bg-indigo-900/30',
    },
    {
      id: 'overrides',
      icon: <History className="w-8 h-8" />,
      label: t('mobileSupervisor.overrideHistory') || 'Override History',
      description: t('mobileSupervisor.overrideHistoryDesc') || 'View past overrides',
      path: '/supervisor/reports?tab=override-history',
      color: 'text-rose-600 dark:text-rose-400',
      bgColor: 'bg-rose-100 dark:bg-rose-900/30',
    },
  ];

  return (
    <div className="flex flex-col gap-4 -mx-4 sm:-mx-6 md:-mx-8 -my-4">
      {/* Quick Stats Bar */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-4 py-3">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
            {t('supervisor.quickStats') || 'Quick Stats'}
          </span>
          <div className="flex items-center gap-2">
            {isLoading && <Loader2 className="w-4 h-4 animate-spin text-gray-400" />}
            <button
              onClick={() => setShowScanOptions(true)}
              className="p-2 rounded-lg bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 hover:bg-purple-200 dark:hover:bg-purple-900/50 transition-colors"
            >
              <QrCode className="w-5 h-5" />
            </button>
          </div>
        </div>
        <div className="grid grid-cols-4 gap-2 text-center">
          <div>
            <div className="text-2xl font-bold text-green-600 dark:text-green-400">{activeSessions}</div>
            <div className="text-xs text-gray-500 dark:text-gray-400">{t('supervisor.active') || 'Active'}</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">{plannedSessions}</div>
            <div className="text-xs text-gray-500 dark:text-gray-400">{t('status.planned') || 'Submitted'}</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">{boxesToday}</div>
            <div className="text-xs text-gray-500 dark:text-gray-400">{t('supervisor.boxes') || 'Boxes'}</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-gray-700 dark:text-gray-300">{enabledStations}/{totalStations}</div>
            <div className="text-xs text-gray-500 dark:text-gray-400">{t('supervisor.stations') || 'Stations'}</div>
          </div>
        </div>
      </div>

      {/* Feature Grid */}
      <div className="px-4 pb-4">
        <div className="grid grid-cols-2 gap-3">
          {features.map((feature) => (
            <button
              key={feature.id}
              onClick={() => feature.id === 'transfer' ? setShowTransferOptions(true) : navigate(feature.path)}
              className="flex flex-col items-center justify-center p-4 bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 active:scale-95 transition-transform"
            >
              <div className={`${feature.bgColor} ${feature.color} p-3 rounded-xl mb-2`}>
                {feature.icon}
              </div>
              <span className="text-sm font-semibold text-gray-900 dark:text-white text-center">
                {feature.label}
              </span>
              <span className="text-xs text-gray-500 dark:text-gray-400 text-center mt-0.5">
                {feature.description}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Scan Options Modal */}
      {showScanOptions && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setShowScanOptions(false)}
          />
          <div className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-xl w-full max-w-sm p-6">
            <button
              onClick={() => setShowScanOptions(false)}
              className="absolute top-4 right-4 p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              <X className="w-5 h-5" />
            </button>

            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-6 text-center">
              {t('mobileSupervisor.scanOptions') || 'What do you want to scan?'}
            </h2>

            <div className="space-y-3">
              <button
                onClick={() => {
                  setShowScanOptions(false);
                  navigate('/inventory?tab=boxLookup');
                }}
                className="w-full flex items-center gap-4 p-4 bg-cyan-50 dark:bg-cyan-900/20 border-2 border-cyan-200 dark:border-cyan-700 rounded-xl hover:bg-cyan-100 dark:hover:bg-cyan-900/30 active:scale-98 transition-all"
              >
                <div className="bg-cyan-100 dark:bg-cyan-800 text-cyan-600 dark:text-cyan-400 p-3 rounded-xl">
                  <Tag className="w-8 h-8" />
                </div>
                <div className="text-left">
                  <div className="font-semibold text-gray-900 dark:text-white">
                    {t('mobileSupervisor.labelLookup') || 'Label Lookup'}
                  </div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">
                    {t('mobileSupervisor.labelLookupDesc') || 'Scan a box label to view details'}
                  </div>
                </div>
              </button>

              <button
                onClick={() => {
                  setShowScanOptions(false);
                  navigate('/supervisor/scan-station');
                }}
                className="w-full flex items-center gap-4 p-4 bg-purple-50 dark:bg-purple-900/20 border-2 border-purple-200 dark:border-purple-700 rounded-xl hover:bg-purple-100 dark:hover:bg-purple-900/30 active:scale-98 transition-all"
              >
                <div className="bg-purple-100 dark:bg-purple-800 text-purple-600 dark:text-purple-400 p-3 rounded-xl">
                  <ScanLine className="w-8 h-8" />
                </div>
                <div className="text-left">
                  <div className="font-semibold text-gray-900 dark:text-white">
                    {t('mobileSupervisor.scanStation') || 'Scan Station'}
                  </div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">
                    {t('mobileSupervisor.scanStationDesc') || 'Scan station QR to view status'}
                  </div>
                </div>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Transfer Options Modal */}
      {showTransferOptions && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setShowTransferOptions(false)}
          />
          <div className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-xl w-full max-w-sm p-6">
            <button
              onClick={() => setShowTransferOptions(false)}
              className="absolute top-4 right-4 p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              <X className="w-5 h-5" />
            </button>

            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-6 text-center">
              {t('mobileSupervisor.transfers') || 'Transfers'}
            </h2>

            <div className="space-y-3">
              <button
                onClick={() => {
                  setShowTransferOptions(false);
                  navigate('/supervisor/transfer');
                }}
                className="w-full flex items-center gap-4 p-4 bg-green-50 dark:bg-green-900/20 border-2 border-green-200 dark:border-green-700 rounded-xl hover:bg-green-100 dark:hover:bg-green-900/30 active:scale-98 transition-all"
              >
                <div className="bg-green-100 dark:bg-green-800 text-green-600 dark:text-green-400 p-3 rounded-xl">
                  <Package className="w-8 h-8" />
                </div>
                <div className="text-left">
                  <div className="font-semibold text-gray-900 dark:text-white">
                    {t('mobileSupervisor.receive') || 'Receive'}
                  </div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">
                    {t('mobileSupervisor.receiveDesc') || 'Receive incoming transfers'}
                  </div>
                </div>
              </button>

              <button
                onClick={() => {
                  setShowTransferOptions(false);
                  navigate('/supervisor/transfers?type=transfer-back');
                }}
                className="w-full flex items-center gap-4 p-4 bg-amber-50 dark:bg-amber-900/20 border-2 border-amber-200 dark:border-amber-700 rounded-xl hover:bg-amber-100 dark:hover:bg-amber-900/30 active:scale-98 transition-all"
              >
                <div className="bg-amber-100 dark:bg-amber-800 text-amber-600 dark:text-amber-400 p-3 rounded-xl">
                  <ArrowLeftRight className="w-8 h-8" />
                </div>
                <div className="text-left">
                  <div className="font-semibold text-gray-900 dark:text-white">
                    {t('mobileSupervisor.transferOption') || 'Transfer'}
                  </div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">
                    {t('mobileSupervisor.transferOptionDesc') || 'Transfer back to warehouse'}
                  </div>
                </div>
              </button>

              <button
                onClick={() => {
                  setShowTransferOptions(false);
                  navigate('/supervisor/transfers');
                }}
                className="w-full flex items-center gap-4 p-4 bg-blue-50 dark:bg-blue-900/20 border-2 border-blue-200 dark:border-blue-700 rounded-xl hover:bg-blue-100 dark:hover:bg-blue-900/30 active:scale-98 transition-all"
              >
                <div className="bg-blue-100 dark:bg-blue-800 text-blue-600 dark:text-blue-400 p-3 rounded-xl">
                  <ListChecks className="w-8 h-8" />
                </div>
                <div className="text-left">
                  <div className="font-semibold text-gray-900 dark:text-white">
                    {t('mobileSupervisor.allTransfers') || 'All Transfers'}
                  </div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">
                    {t('mobileSupervisor.allTransfersDesc') || 'View all transfer orders'}
                  </div>
                </div>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MobileSupervisorDashboard;
